
import { useState } from "react";
import { uploadFileToStorage } from "@/utils/file-storage";
import { toast } from "sonner";

interface UseCommissionImageUploadReturn {
  uploadImage: (file: File) => Promise<string | null>;
  isUploading: boolean;
  error: string | null;
}

/**
 * Hook for uploading commission example images to storage
 */
export function useCommissionImageUpload(artistId?: string): UseCommissionImageUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!artistId) {
      setError("Artist ID is required for upload");
      toast.error("Erro: ID do artista é necessário para upload");
      return null;
    }

    try {
      setIsUploading(true);
      setError(null);
      
      // Validate file type
      const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
      if (!validTypes.includes(file.type)) {
        throw new Error("Tipo de arquivo não suportado. Use JPEG, PNG, WEBP ou GIF.");
      }
      
      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error("O tamanho do arquivo não pode exceder 5MB.");
      }
      
      // Upload to storage
      const folder = "commission-examples";
      const url = await uploadFileToStorage(file, folder, artistId);
      
      if (!url) {
        throw new Error("Erro ao fazer upload da imagem");
      }
      
      return url;
    } catch (error: any) {
      setError(error.message || "Erro ao fazer upload da imagem");
      toast.error(error.message || "Erro ao fazer upload da imagem");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadImage,
    isUploading,
    error
  };
}
